(() => {
  const initNasLab = (root) => {
    if (root.dataset.foldinglinkInit === 'true') return;
    root.dataset.foldinglinkInit = 'true';
    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const stage = root.querySelector('[data-foldinglink-nas-stage]');
    const module = root.querySelector('[data-foldinglink-nas-module]');
    const nas = root.querySelector('[data-foldinglink-nas-node]');
    const hotspot = root.querySelector('[data-foldinglink-nas-hotspot]');
    const phone = root.querySelector('[data-foldinglink-nas-phone]');
    const phoneAction = root.querySelector('[data-foldinglink-nas-phone-action]');
    const action = root.querySelector('[data-foldinglink-nas-action]');
    const reset = root.querySelector('[data-foldinglink-nas-reset]');
    const templates = Array.from(root.querySelectorAll('template[data-foldinglink-nas-step]'));
    const label = root.querySelector('[data-foldinglink-step-label]');
    const instruction = root.querySelector('[data-foldinglink-step-instruction]');
    const title = root.querySelector('[data-foldinglink-step-title]');
    const text = root.querySelector('[data-foldinglink-step-text]');
    const benefits = root.querySelector('[data-foldinglink-step-benefits]');
    const files = Array.from(root.querySelectorAll('.foldinglink-nas-lab__file'));
    const targets = [nas, ...root.querySelectorAll('.foldinglink-nas-lab__node--peer')];
    const phoneRouteSvg = root.querySelector('[data-foldinglink-nas-phone-route]');
    const phoneRouteLine = root.querySelector('[data-foldinglink-nas-phone-route-line]');
    const phoneRouteFlow = root.querySelector('[data-foldinglink-nas-phone-route-flow]');
    const peerRouteFlows = Array.from(root.querySelectorAll('[data-foldinglink-nas-peer-flow]'));
    const animations = new Set();
    let state = 0;
    let frame = 0;
    let routeFrame = 0;
    let routeTimer = 0;
    let pointerStart = null;
    let pointerDragged = false;
    let moduleSnapActive = false;
    let moduleReturnTimer = 0;
    let suppressModuleClick = false;
    let dragProxy = null;
    let dragRestoreFrame = 0;

    const removeDragProxy = () => {
      const needsRestore = Boolean(dragProxy || module?.classList.contains('is-drag-origin'));
      if (!needsRestore) return;
      window.cancelAnimationFrame(dragRestoreFrame);
      module?.classList.add('is-drag-restoring');
      module?.classList.remove('is-drag-origin');
      dragProxy?.remove();
      dragProxy = null;
      dragRestoreFrame = window.requestAnimationFrame(() => {
        module?.classList.remove('is-drag-restoring');
        dragRestoreFrame = 0;
      });
    };

    const createDragProxy = (rect) => {
      if (!module || dragProxy || !rect) return dragProxy;
      dragProxy = module.cloneNode(true);
      dragProxy.removeAttribute('data-foldinglink-nas-module');
      dragProxy.removeAttribute('aria-label');
      dragProxy.setAttribute('aria-hidden', 'true');
      dragProxy.setAttribute('inert', '');
      dragProxy.tabIndex = -1;
      dragProxy.disabled = true;
      dragProxy.classList.add('foldinglink-nas-lab__module--drag-proxy');
      dragProxy.style.setProperty('--foldinglink-drag-proxy-left', `${rect.left}px`);
      dragProxy.style.setProperty('--foldinglink-drag-proxy-top', `${rect.top}px`);
      dragProxy.style.setProperty('--foldinglink-drag-proxy-width', `${rect.width}px`);
      dragProxy.style.setProperty('--foldinglink-drag-proxy-height', `${rect.height}px`);
      document.body.appendChild(dragProxy);
      module.classList.add('is-drag-origin');
      return dragProxy;
    };

    const cancelRouteSchedule = () => {
      window.clearTimeout(routeTimer);
      window.cancelAnimationFrame(routeFrame);
      routeTimer = 0;
      routeFrame = 0;
    };

    const cancelAnimations = () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(dragRestoreFrame);
      cancelRouteSchedule();
      window.clearTimeout(moduleReturnTimer);
      frame = 0;
      dragRestoreFrame = 0;
      moduleReturnTimer = 0;
      removeDragProxy();
      animations.forEach((animation) => animation.cancel());
      animations.clear();
    };

    const resetFiles = (complete = false) => {
      cancelAnimations();
      root.classList.toggle('is-transfer-complete', complete);
      files.forEach((file) => {
        file.style.removeProperty('transform');
        file.style.opacity = complete ? '0' : '';
      });
    };

    const updatePhoneRoute = () => {
      if (!stage || !phone || !nas || !phoneRouteSvg || !phoneRouteLine || !phoneRouteFlow) return;
      const stageRect = stage.getBoundingClientRect();
      const phoneRect = phone.getBoundingClientRect();
      const nasRect = nas.getBoundingClientRect();
      if (!stageRect.width || !stageRect.height || !phoneRect.width || !nasRect.width) return;
      const start = {
        x: phoneRect.right - stageRect.left,
        y: phoneRect.top - stageRect.top + (phoneRect.height * .5)
      };
      const end = {
        x: nasRect.left - stageRect.left + (nasRect.width * .02),
        y: start.y
      };
      const pathData = `M${start.x.toFixed(2)} ${start.y.toFixed(2)}L${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
      phoneRouteSvg.setAttribute('viewBox', `0 0 ${stageRect.width.toFixed(2)} ${stageRect.height.toFixed(2)}`);
      phoneRouteLine.setAttribute('d', pathData);
      phoneRouteFlow.setAttribute('d', pathData);
    };

    const schedulePhoneRoute = ({ delay = 0, reveal = false } = {}) => {
      cancelRouteSchedule();
      if (reveal) stage?.classList.remove('is-phone-route-ready');
      if (state < 3) {
        stage?.classList.remove('is-phone-route-ready');
        return;
      }
      routeTimer = window.setTimeout(() => {
        routeTimer = 0;
        routeFrame = window.requestAnimationFrame(() => {
          routeFrame = 0;
          updatePhoneRoute();
          if (state >= 3) stage?.classList.add('is-phone-route-ready');
        });
      }, delay);
    };

    const trackTransferAnimation = (animation, onFinish) => {
      animations.add(animation);
      animation.addEventListener('finish', () => {
        animations.delete(animation);
        if (state !== 4) return;
        onFinish?.();
        animation.cancel();
        if (animations.size === 0) root.classList.add('is-transfer-complete');
      }, { once: true });
    };

    const animateRouteFlow = (path, delay, duration) => {
      if (!path) return;
      const travel = duration > 1000 ? -200 : -64;
      const animation = path.animate([
        { opacity: 0, strokeDashoffset: '0' },
        { opacity: 1, strokeDashoffset: String(travel * .12), offset: .12 },
        { opacity: 1, strokeDashoffset: String(travel * .86), offset: .86 },
        { opacity: 0, strokeDashoffset: String(travel) }
      ], {
        duration,
        delay,
        easing: 'linear'
      });
      trackTransferAnimation(animation);
    };

    const beginFileTransfer = () => {
      resetFiles(false);
      updatePhoneRoute();
      stage?.classList.add('is-phone-route-ready');
      if (reducedMotion.matches || typeof Element.prototype.animate !== 'function') {
        resetFiles(true);
        return;
      }

      animateRouteFlow(phoneRouteFlow, 0, 2000);

      frame = window.requestAnimationFrame(() => {
        frame = window.requestAnimationFrame(() => {
          if (state !== 4 || !nas) return;
          const nasRect = nas.getBoundingClientRect();
          const nasCenter = {
            x: nasRect.left + (nasRect.width / 2),
            y: nasRect.top + (nasRect.height / 2)
          };

          files.forEach((file, index) => {
            const fileDelay = index > 0 ? (index - 1) * 400 : 0;
            const fileRect = file.getBoundingClientRect();
            const target = targets[index] || nas;
            const targetRect = target.getBoundingClientRect();
            const origin = {
              x: fileRect.left + (fileRect.width / 2),
              y: fileRect.top + (fileRect.height / 2)
            };
            const via = {
              x: nasCenter.x - origin.x,
              y: nasCenter.y - origin.y
            };
            const destination = {
              x: targetRect.left + (targetRect.width / 2) - origin.x,
              y: targetRect.top + (targetRect.height / 2) - origin.y
            };
            const animation = file.animate([
              { transform: 'translate3d(0, 0, 0) scale(1)', opacity: 0 },
              { transform: 'translate3d(0, 0, 0) scale(1)', opacity: 1, offset: 0.12 },
              { transform: `translate3d(${via.x}px, ${via.y}px, 0) scale(.82)`, opacity: 1, offset: 0.56 },
              { transform: `translate3d(${destination.x}px, ${destination.y}px, 0) scale(.58)`, opacity: 0 }
            ], {
              duration: 1160,
              delay: fileDelay,
              easing: 'cubic-bezier(.77, 0, .175, 1)',
              fill: 'forwards'
            });
            trackTransferAnimation(animation, () => {
              file.style.opacity = '0';
            });
            if (index > 0) animateRouteFlow(peerRouteFlows[index - 1], 620 + fileDelay, 380);
          });
        });
      });
    };

    const updateButtonText = (button, value) => {
      if (!button) return;
      const buttonText = button.querySelector('.xo-btn__text');
      if (buttonText) {
        buttonText.textContent = value;
        if (buttonText.hasAttribute('data-text')) buttonText.dataset.text = value;
      }
      if (value) button.setAttribute('aria-label', value);
      else button.removeAttribute('aria-label');
    };

    const render = (index, { animateTransfer = false } = {}) => {
      const template = templates[index];
      if (!template) return;
      const source = template.content.firstElementChild;
      state = index;
      if (label) label.textContent = source.dataset.label || '';
      if (instruction) instruction.textContent = source.dataset.instruction || '';
      if (title) {
        title.textContent = source.dataset.title || '';
        title.hidden = !source.dataset.title;
      }
      if (text) {
        text.textContent = source.dataset.text || '';
        text.hidden = !source.dataset.text;
      }
      if (benefits) benefits.innerHTML = source.innerHTML;
      const actionLabel = source.dataset.actionLabel || '';
      updateButtonText(action, actionLabel);
      if (action) action.hidden = index !== 2 || !actionLabel;
      updateButtonText(phoneAction, actionLabel);
      if (phoneAction) phoneAction.hidden = index !== 3 || !actionLabel;
      if (module) {
        module.disabled = index !== 0;
        module.classList.remove('is-dragging', 'is-snapped', 'is-returning', 'is-drag-origin');
        module.style.removeProperty('transform');
      }
      removeDragProxy();
      stage?.classList.remove('is-module-dragging', 'is-module-drop-active');
      pointerStart = null;
      pointerDragged = false;
      moduleSnapActive = false;
      if (nas) {
        nas.setAttribute('aria-disabled', String(index !== 1));
      }
      if (hotspot) {
        const hotspotLabel = source.dataset.instruction || stage.getAttribute('aria-label') || '';
        hotspot.disabled = index !== 1;
        hotspot.tabIndex = index === 1 ? 0 : -1;
        hotspot.setAttribute('aria-hidden', String(index !== 1));
        if (hotspotLabel) hotspot.setAttribute('aria-label', hotspotLabel);
        else hotspot.removeAttribute('aria-label');
      }
      if (phone) {
        const phoneVisible = index >= 3;
        phone.setAttribute('aria-hidden', String(!phoneVisible));
      }
      stage.dataset.step = String(index);
      root.dataset.step = String(index);
      if (index === 4 && animateTransfer) {
        beginFileTransfer();
      } else {
        resetFiles(index === 4);
        schedulePhoneRoute({
          delay: index === 3 && !reducedMotion.matches ? 320 : 0,
          reveal: index === 3
        });
      }
    };

    root.foldinglinkSelectStep = (blockId) => {
      const index = templates.findIndex((template) => template.dataset.blockId === blockId);
      if (index >= 0) render(index);
    };

    const advanceFromModule = () => {
      if (state === 0) render(Math.min(1, templates.length - 1));
    };
    const resolveModuleDrop = (clientX, clientY) => {
      const nasRect = nas?.getBoundingClientRect();
      if (!nasRect || !pointerStart?.rect) return { active: false, x: 0, y: 0 };
      const x = clientX - pointerStart.x;
      const y = clientY - pointerStart.y;
      const moduleCenter = {
        x: pointerStart.rect.left + (pointerStart.rect.width / 2) + x,
        y: pointerStart.rect.top + (pointerStart.rect.height / 2) + y
      };
      const snapRange = Math.max(10, Math.min(22, nasRect.width * .035));
      const active = moduleCenter.x >= nasRect.left - snapRange
        && moduleCenter.x <= nasRect.right + snapRange
        && moduleCenter.y >= nasRect.top - snapRange
        && moduleCenter.y <= nasRect.bottom + snapRange;
      return {
        active,
        x: nasRect.left + (nasRect.width / 2) - pointerStart.rect.left - (pointerStart.rect.width / 2),
        y: nasRect.top + (nasRect.height / 2) - pointerStart.rect.top - (pointerStart.rect.height / 2)
      };
    };
    module?.addEventListener('click', (event) => {
      if (suppressModuleClick) {
        event.preventDefault();
        return;
      }
      advanceFromModule();
    }, options);
    module?.addEventListener('pointerdown', (event) => {
      if (state !== 0 || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      window.clearTimeout(moduleReturnTimer);
      moduleReturnTimer = 0;
      module.classList.remove('is-returning', 'is-snapped');
      module.style.removeProperty('transform');
      pointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        rect: module.getBoundingClientRect()
      };
      pointerDragged = false;
      moduleSnapActive = false;
      removeDragProxy();
      module.classList.add('is-dragging');
      stage?.classList.add('is-module-dragging');
      try { module.setPointerCapture?.(event.pointerId); } catch (_) {}
    }, options);
    module?.addEventListener('pointermove', (event) => {
      if (!pointerStart || pointerStart.id !== event.pointerId) return;
      const x = event.clientX - pointerStart.x;
      const y = event.clientY - pointerStart.y;
      if (!pointerDragged && Math.hypot(x, y) < 6) return;
      event.preventDefault();
      pointerDragged = true;
      const proxy = createDragProxy(pointerStart.rect);
      let dragX = x;
      let dragY = y;
      const drop = resolveModuleDrop(event.clientX, event.clientY);

      if (drop.active) {
        dragX = drop.x;
        dragY = drop.y;
      }

      moduleSnapActive = drop.active;
      proxy?.classList.toggle('is-snapped', drop.active);
      stage?.classList.toggle('is-module-drop-active', drop.active);
      if (proxy) proxy.style.transform = `translate3d(${dragX}px, ${dragY}px, 0)`;
    }, options);
    const finishModulePointer = (event, cancelled = false) => {
      if (!pointerStart || pointerStart.id !== event.pointerId) return;
      const wasDragged = pointerDragged;
      const droppedOnNas = !cancelled && wasDragged && resolveModuleDrop(event.clientX, event.clientY).active;
      try { module.releasePointerCapture?.(event.pointerId); } catch (_) {}
      pointerStart = null;
      pointerDragged = false;
      moduleSnapActive = false;
      module.classList.remove('is-dragging', 'is-snapped');
      stage?.classList.remove('is-module-dragging', 'is-module-drop-active');
      if (!wasDragged) {
        removeDragProxy();
        return;
      }
      suppressModuleClick = true;
      window.setTimeout(() => { suppressModuleClick = false; }, 0);
      if (droppedOnNas) {
        removeDragProxy();
        advanceFromModule();
        return;
      }

      dragProxy?.classList.add('is-returning');
      if (dragProxy) dragProxy.style.transform = 'translate3d(0, 0, 0)';
      const settleModule = () => {
        removeDragProxy();
        moduleReturnTimer = 0;
      };
      if (reducedMotion.matches) settleModule();
      else moduleReturnTimer = window.setTimeout(settleModule, 190);
    };
    module?.addEventListener('pointerup', (event) => finishModulePointer(event), options);
    module?.addEventListener('pointercancel', (event) => finishModulePointer(event, true), options);
    module?.addEventListener('dragstart', (event) => {
      event.preventDefault();
    }, options);
    hotspot?.addEventListener('click', () => {
      if (state === 1) render(Math.min(2, templates.length - 1));
    }, options);
    action?.addEventListener('click', () => {
      if (state === 2) render(Math.min(3, templates.length - 1));
    }, options);
    phoneAction?.addEventListener('click', () => {
      if (state === 3) render(Math.min(4, templates.length - 1), { animateTransfer: true });
    }, options);
    reset?.addEventListener('click', () => render(0), options);

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
        if (state === 4 && animations.size) resetFiles(true);
        schedulePhoneRoute();
      })
      : null;
    if (stage) resizeObserver?.observe(stage);
    reducedMotion.addEventListener?.('change', () => {
      if (state === 4) resetFiles(true);
      schedulePhoneRoute();
    }, options);

    root.foldinglinkDestroyNasLab = () => {
      abortController.abort();
      resizeObserver?.disconnect();
      resetFiles(false);
      delete root.foldinglinkSelectStep;
      delete root.foldinglinkDestroyNasLab;
      delete root.dataset.foldinglinkInit;
    };
    render(0);
  };

  const initCompare = (root) => {
    if (root.dataset.foldinglinkInit === 'true') return;
    root.dataset.foldinglinkInit = 'true';
    const range = root.querySelector('[data-foldinglink-compare-range]');
    if (!range) return;
    const update = () => root.style.setProperty('--foldinglink-split', `${range.value}%`);
    range.addEventListener('input', update, { passive: true });
    update();
  };

  const initFeedbackCarousel = (root) => {
    if (root.dataset.foldinglinkFeedbackInit === 'true') return;
    const viewport = root.querySelector('[data-foldinglink-feedback-viewport]');
    const track = root.querySelector('[data-foldinglink-feedback-track]');
    const controls = root.querySelector('[data-foldinglink-feedback-controls]');
    const previousButton = root.querySelector('[data-foldinglink-feedback-prev]');
    const nextButton = root.querySelector('[data-foldinglink-feedback-next]');
    const slides = Array.from(root.querySelectorAll('[data-foldinglink-feedback-slide]'));
    if (!viewport || !track || !slides.length) return;

    root.dataset.foldinglinkFeedbackInit = 'true';
    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let updateFrame = 0;
    let scrollSettleTimer = 0;
    let activeScrollTarget = null;

    const applyBoundaryState = (position, maxScroll) => {
      const canScroll = maxScroll > 2;
      if (previousButton) previousButton.disabled = !canScroll || position <= 2;
      if (nextButton) nextButton.disabled = !canScroll || position >= maxScroll - 2;
    };

    const updateControls = () => {
      updateFrame = 0;
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const canScroll = maxScroll > 2;
      if (controls) controls.hidden = !canScroll;
      root.classList.toggle('is-scrollable', canScroll);
      applyBoundaryState(activeScrollTarget ?? viewport.scrollLeft, maxScroll);
    };

    const scheduleControlsUpdate = () => {
      window.cancelAnimationFrame(updateFrame);
      updateFrame = window.requestAnimationFrame(updateControls);
    };

    const completeScrollTransition = () => {
      window.clearTimeout(scrollSettleTimer);
      scrollSettleTimer = 0;
      activeScrollTarget = null;
      scheduleControlsUpdate();
    };

    const animateTo = (target, maxScroll) => {
      window.clearTimeout(scrollSettleTimer);
      activeScrollTarget = target;
      applyBoundaryState(target, maxScroll);
      if (reducedMotion.matches || Math.abs(target - viewport.scrollLeft) < 1) {
        viewport.scrollLeft = target;
        completeScrollTransition();
        return;
      }
      viewport.scrollTo({ left: target, behavior: 'smooth' });
      scrollSettleTimer = window.setTimeout(completeScrollTransition, 500);
    };

    const scrollByPage = (direction) => {
      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const currentPosition = activeScrollTarget ?? viewport.scrollLeft;
      const target = Math.min(maxScroll, Math.max(0, currentPosition + (direction * (viewport.clientWidth + gap))));
      animateTo(target, maxScroll);
    };

    previousButton?.addEventListener('click', () => scrollByPage(-1), options);
    nextButton?.addEventListener('click', () => scrollByPage(1), options);
    viewport.addEventListener('scroll', scheduleControlsUpdate, { passive: true, signal: abortController.signal });
    viewport.addEventListener('scrollend', completeScrollTransition, options);
    viewport.addEventListener('pointerdown', completeScrollTransition, options);
    viewport.addEventListener('wheel', completeScrollTransition, { passive: true, signal: abortController.signal });

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(scheduleControlsUpdate)
      : null;
    resizeObserver?.observe(viewport);
    resizeObserver?.observe(track);

    root.foldinglinkSelectFeedback = (blockId) => {
      const slide = slides.find((item) => item.dataset.foldinglinkFeedbackSlide === blockId);
      if (!slide) return;
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const slideStart = slide.offsetLeft - track.offsetLeft;
      const slideEnd = slideStart + slide.offsetWidth;
      const visibleStart = viewport.scrollLeft;
      const visibleEnd = visibleStart + viewport.clientWidth;
      if (slideStart >= visibleStart - 2 && slideEnd <= visibleEnd + 2) return;
      const target = Math.min(maxScroll, Math.max(0, slideStart));
      animateTo(target, maxScroll);
    };

    root.foldinglinkDestroyFeedback = () => {
      abortController.abort();
      resizeObserver?.disconnect();
      window.cancelAnimationFrame(updateFrame);
      window.clearTimeout(scrollSettleTimer);
      delete root.foldinglinkSelectFeedback;
      delete root.foldinglinkDestroyFeedback;
      delete root.dataset.foldinglinkFeedbackInit;
    };

    viewport.scrollLeft = 0;
    scheduleControlsUpdate();
  };

  const initTabs = (root) => {
    if (root.dataset.foldinglinkInit === 'true') return;
    root.dataset.foldinglinkInit = 'true';
    const playButton = root.querySelector('[data-foldinglink-demo-play]');
    const playLabel = root.querySelector('[data-foldinglink-demo-play-label]');
    const panelRail = root.querySelector('.foldinglink-demo-tabs__panels');
    const panels = Array.from(root.querySelectorAll('[data-foldinglink-tab-panel]'));
    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    let activeId = '';
    let pointerId = null;
    let pointerStartX = 0;
    let pointerStartY = 0;

    const activePanel = () => panels.find((panel) => panel.dataset.foldinglinkTabPanel === activeId);
    const updatePlayState = () => {
      const video = activePanel()?.querySelector('video');
      const playing = Boolean(video && !video.paused && !video.ended);
      playButton?.classList.toggle('is-playing', playing);
      playButton?.setAttribute('aria-pressed', String(playing));
    };

    const activate = (id) => {
      activeId = id;
      let selectedPanel;
      panels.forEach((panel) => {
        const selected = panel.dataset.foldinglinkTabPanel === id;
        panel.classList.toggle('is-active', selected);
        panel.hidden = !selected;
        if (selected) selectedPanel = panel;
        else panel.querySelectorAll('video').forEach((video) => video.pause());
      });
      const label = selectedPanel?.getAttribute('aria-label') || '';
      if (playLabel) playLabel.textContent = label;
      if (playButton) playButton.setAttribute('aria-label', `Play ${label} demo`);
      updatePlayState();
    };

    const activateRelative = (direction) => {
      if (!panels.length) return;
      const currentIndex = Math.max(0, panels.findIndex((panel) => panel.dataset.foldinglinkTabPanel === activeId));
      const nextIndex = (currentIndex + direction + panels.length) % panels.length;
      activate(panels[nextIndex].dataset.foldinglinkTabPanel);
    };

    playButton?.addEventListener('click', () => {
      const video = activePanel()?.querySelector('video');
      if (!video) return;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
      requestAnimationFrame(updatePlayState);
    }, options);
    playButton?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      activateRelative(event.key === 'ArrowRight' ? 1 : -1);
    }, options);

    panelRail?.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
    }, options);
    panelRail?.addEventListener('pointerup', (event) => {
      if (pointerId !== event.pointerId) return;
      const deltaX = event.clientX - pointerStartX;
      const deltaY = event.clientY - pointerStartY;
      pointerId = null;
      if (Math.abs(deltaX) >= 44 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
        activateRelative(deltaX < 0 ? 1 : -1);
      }
    }, options);
    panelRail?.addEventListener('pointercancel', () => { pointerId = null; }, options);
    panels.forEach((panel) => panel.querySelectorAll('video').forEach((video) => {
      video.addEventListener('play', updatePlayState, options);
      video.addEventListener('pause', updatePlayState, options);
    }));

    const initialIndex = Number.parseInt(root.dataset.foldinglinkInitialTab, 10) - 1;
    const initialPanel = panels[initialIndex] || panels[0];
    if (initialPanel) activate(initialPanel.dataset.foldinglinkTabPanel);
    root.foldinglinkSelectTab = activate;
    root.foldinglinkDestroyTabs = () => {
      abortController.abort();
      panels.forEach((panel) => panel.querySelectorAll('video').forEach((video) => video.pause()));
      delete root.foldinglinkSelectTab;
      delete root.foldinglinkDestroyTabs;
      delete root.dataset.foldinglinkInit;
    };
  };

  const init = (scope = document) => {
    const roots = (selector) => [
      ...(scope.matches?.(selector) ? [scope] : []),
      ...scope.querySelectorAll(selector)
    ];
    roots('[data-foldinglink-nas-lab]').forEach(initNasLab);
    roots('[data-foldinglink-compare]').forEach(initCompare);
    roots('[data-foldinglink-feedback-carousel]').forEach(initFeedbackCarousel);
    roots('[data-foldinglink-tabs]').forEach(initTabs);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const nasLab = event.target.matches?.('[data-foldinglink-nas-lab]')
      ? event.target
      : event.target.querySelector?.('[data-foldinglink-nas-lab]');
    const tabs = event.target.matches?.('[data-foldinglink-tabs]')
      ? event.target
      : event.target.querySelector?.('[data-foldinglink-tabs]');
    const feedback = event.target.matches?.('[data-foldinglink-feedback-carousel]')
      ? event.target
      : event.target.querySelector?.('[data-foldinglink-feedback-carousel]');
    nasLab?.foldinglinkDestroyNasLab?.();
    tabs?.foldinglinkDestroyTabs?.();
    feedback?.foldinglinkDestroyFeedback?.();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;
    const section = event.target.closest?.('[data-foldinglink-nas-lab], [data-foldinglink-tabs]') || event.target.querySelector?.('[data-foldinglink-nas-lab], [data-foldinglink-tabs]');
    section?.foldinglinkSelectStep?.(blockId);
    section?.foldinglinkSelectTab?.(blockId);
    const feedback = event.target.closest?.('[data-foldinglink-feedback-carousel]') || event.target.querySelector?.('[data-foldinglink-feedback-carousel]');
    feedback?.foldinglinkSelectFeedback?.(blockId);
  });
})();
